import { cva, type VariantProps } from 'class-variance-authority'
import React from 'react'

import { cn } from '../../../utils/cn'
import { Link } from '../../Link'

const containerVariants = cva('flex items-center', {
  variants: {
    tabletOnly: {
      true: 'pc:hidden',
    },
    pcOnly: {
      true: 'hidden pc:flex',
    },
  },
})

const buttonClasses = cn(
  'flex cursor-pointer items-center bg-transparent text-blue-01 no-underline',
  'px-6 py-4 typography-button2',
  'pc:px-8 pc:py-4 pc:typography-button1',
)

const IconBefore = ({ children }: { children: React.ReactNode }) => (
  <div className="mr-1 flex items-center justify-center [&_svg]:size-6">
    {children}
  </div>
)

const IconAfter = ({ children }: { children: React.ReactNode }) => (
  <div className="ml-1 flex items-center justify-center [&_svg]:size-6">
    {children}
  </div>
)

export interface ButtonListItemProps
  extends VariantProps<typeof containerVariants> {
  /**
   * Icon before children
   */
  iconBefore?: React.ReactNode

  /**
   * Icon after children
   */
  iconAfter?: React.ReactNode

  /**
   * href attribute
   */
  href?: string

  /**
   * Link target
   */
  target?: string

  /**
   * Is external link
   */
  external?: boolean

  /**
   * Button type
   */
  type?: 'button' | 'submit' | 'reset'

  /**
   * Content
   */
  children?: React.ReactNode

  /**
   * Click handler
   */
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
}

/**
 * ButtonListItem component
 */
export const ButtonListItem = ({
  iconBefore,
  iconAfter,
  href,
  target,
  external,
  type,
  tabletOnly,
  pcOnly,
  children,
  onClick,
}: ButtonListItemProps) => {
  const innerContent = (
    <>
      {iconBefore && <IconBefore>{iconBefore}</IconBefore>}
      {children}
      {iconAfter && <IconAfter>{iconAfter}</IconAfter>}
    </>
  )

  const content = href ? (
    <span className={buttonClasses}>{innerContent}</span>
  ) : (
    <button type={type ?? 'button'} onClick={onClick} className={buttonClasses}>
      {innerContent}
    </button>
  )

  const button = href ? (
    <Link href={href} external={external} target={target}>
      {content}
    </Link>
  ) : (
    content
  )

  return <li className={containerVariants({ tabletOnly, pcOnly })}>{button}</li>
}

ButtonListItem.displayName = 'ButtonListItem'
